import { type TypeExtends, type TypeNotExtends } from '../../../../../../../types/helper'
import { debug, error, warn } from '../../../../../../../utils/logger'
import { type DetailsTargetOptions } from '../../../../settings/details-target-options'
import { type DefaultActivationDetailsRenderer } from '../details'
import { getBottomPosition } from './modes/bottom'
import { getFullscreenPosition } from './modes/fullscreen'
import { getOverlayPosition } from './modes/overlay'
import { getOverlayAreaPosition } from './modes/overlay-area'
import { isZeroValue, normalizeCSSUnitObject } from '../../../../../../../utils/dom-style'
import { type EmbedCSSStyles } from '../../../../../../style'
import { getOuterOverlayAreaPosition } from './modes/outer-overlay-area'

export type GetPositionFn<tPosition extends string> = (option: TypeExtends<DetailsTargetOptions, { mode: tPosition }>, context: GetDetailsElementBoundingContext) => { elementBounding: ElementBoundingStyle, containerStyle?: EmbedCSSStyles }

export interface ElementBoundingStyle { top: number, left: number, width: number, height: number }

// Lazy workaround for dependency injection
// fix it if this function is used in multiple places
export type GetDetailsElementBoundingContext = Pick<DefaultActivationDetailsRenderer, 'windowSize' | 'videoElBounding' | 'ctx' | 'detailsTargetMap' | 'currentActivationPosition' | 'currentLayout'>

/**
   * Computes element bounding to be applied to details component based on target mode
   * @returns details element bounding
   */
export const getDetailsElementBounding = (
  targetOptions: TypeNotExtends<DetailsTargetOptions, { mode: 'in-place' }>,
  context: GetDetailsElementBoundingContext
): { elementBounding: ElementBoundingStyle, containerStyle?: EmbedCSSStyles } | undefined => {
  try {
    if (targetOptions.mode === 'fullscreen') {
      return getFullscreenPosition(targetOptions, context)
    }
    if (targetOptions.mode === 'overlay') {
      return getOverlayPosition(targetOptions, context)
    }
    if (targetOptions.mode === 'overlay-area') {
      return getOverlayAreaPosition(targetOptions, context)
    }
    if (targetOptions.mode === 'bottom') {
      return getBottomPosition(targetOptions, context)
    }
    if (targetOptions.mode === 'outer-overlay-area') {
      return getOuterOverlayAreaPosition(targetOptions, context)
    }
    error('Unknown details position', targetOptions)
    return undefined
  } catch (err) {
    error('Error while computing details element bounding', err)
    return undefined
  }
}

export const getContainerStyle = (styles: { elementBounding?: ElementBoundingStyle, containerStyle?: EmbedCSSStyles } = {}, context: GetDetailsElementBoundingContext) => {
  const {
    elementBounding,
    containerStyle
  } = styles
  if (elementBounding == null) {
    warn('Details element bounding could not be determined, hiding.')
    return { display: 'none' }
  }
  const style: EmbedCSSStyles = {
    ...normalizeCSSUnitObject(elementBounding),
    ...containerStyle,
    ...context.currentLayout.value.details?.containerStyle,
    position: 'fixed'
  }
  if (isZeroValue(style.height) || isZeroValue(style.width)) {
    debug('Details element has no height or width. This can happen between transition. Hiding details.', style)
    return { display: 'none' }
  }
  return style
}
